import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { ChartColumnIcon, LoaderIcon } from "lucide-react";
import type { EnvironmentId, ProjectDocumentsUsageTotal } from "@t3tools/contracts";

import { readEnvironmentApi } from "~/environmentApi";
import { selectProjectsAcrossEnvironments, useStore } from "../../store";
import type { Project } from "../../types";
import { SettingsPageContainer, SettingsSection } from "./settingsLayout";
import {
  formatCostUsd,
  formatTokens,
  formatTokensMillions,
  sumUsageTotals,
} from "./UsageSettings.logic";

interface UsageRowView {
  key: string;
  name: string;
  usage: ProjectDocumentsUsageTotal;
}

interface UsageReportView {
  rows: UsageRowView[];
  total: ProjectDocumentsUsageTotal;
}

export function UsageSettings() {
  const { t, i18n } = useLingui();
  const projects = useStore(useShallow(selectProjectsAcrossEnvironments));
  const [report, setReport] = useState<UsageReportView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const grouped = new Map<EnvironmentId, Project[]>();
    for (const project of projects) {
      const members = grouped.get(project.environmentId);
      if (members) {
        members.push(project);
      } else {
        grouped.set(project.environmentId, [project]);
      }
    }

    const run = async () => {
      const rows: UsageRowView[] = [];
      const totals: ProjectDocumentsUsageTotal[] = [];
      for (const [environmentId, members] of grouped) {
        const api = readEnvironmentApi(environmentId);
        if (!api) continue;
        const result = await api.projects.usageReport({
          cwds: members.map((member) => member.cwd),
        });
        result.rows.forEach((row, index) => {
          const member = members[index];
          if (!member) return;
          rows.push({
            key: `${environmentId} ${member.cwd}`,
            name: member.name,
            usage: row.usage,
          });
        });
        totals.push(result.total);
      }
      if (cancelled) return;
      rows.sort((left, right) => right.usage.totalTokens - left.usage.totalTokens);
      setReport({ rows, total: sumUsageTotals(totals) });
      setLoadError(null);
    };

    setReport(null);
    setLoadError(null);
    run().catch((cause: unknown) => {
      if (cancelled) return;
      setLoadError(cause instanceof Error ? cause.message : String(cause));
    });
    return () => {
      cancelled = true;
    };
  }, [projects]);

  const locale = i18n.locale;

  return (
    <SettingsPageContainer>
      <SettingsSection
        title={t`OCR usage`}
        icon={<ChartColumnIcon className="size-3.5" aria-hidden />}
      >
        {loadError !== null ? (
          <p className="px-4 py-8 text-center text-[12px] text-red-400/80">{loadError}</p>
        ) : report === null ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground/40">
            <LoaderIcon className="size-4 animate-spin" />
          </div>
        ) : report.rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-[12px] text-muted-foreground/40">
            <Trans>No projects yet.</Trans>
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-[0.06em] text-muted-foreground/50">
                  <th className="px-4 py-2.5 font-medium">
                    <Trans>Project</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Documents</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Pages</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Paid pages</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Tokens, M</Trans>
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium">
                    <Trans>Cost</Trans>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {report.rows.map((row) => (
                  <tr key={row.key} className="align-top">
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-foreground">{row.name}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {formatTokens(row.usage.docs, locale)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {formatTokens(row.usage.pages, locale)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {formatTokens(row.usage.llmPages, locale)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {formatTokensMillions(row.usage.totalTokens, locale)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {formatCostUsd(row.usage.costUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t text-foreground">
                  <td className="px-4 py-2.5">
                    <span className="font-semibold">
                      <Trans>Total</Trans>
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                    {formatTokens(report.total.docs, locale)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                    {formatTokens(report.total.pages, locale)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                    {formatTokens(report.total.llmPages, locale)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                    {formatTokensMillions(report.total.totalTokens, locale)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums">
                    {formatCostUsd(report.total.costUsd)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
